import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fa_user") || "null"); }
    catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("fa_token"));
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fa_tenant") || "null"); }
    catch { return null; }
  });

  // First-load: detect ?tenant=<slug> query param and apply tenant branding immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("tenant");
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("fa_ref_code", ref.toUpperCase());
    }
    if (!slug) return;
    // Persist slug for slug-locked logins
    localStorage.setItem("fa_slug", slug);
    api.get(`/public/tenant/${slug}`)
      .then((r) => {
        if (r.data?.id) {
          localStorage.setItem("fa_tenant", JSON.stringify(r.data));
          setTenant(r.data);
        }
      })
      .catch(() => {});
  }, []);

  const login = useCallback(async (phone, otp, role, tenant_id, extras = {}) => {
    const lockedSlug = localStorage.getItem("fa_slug");
    let effectiveTenantId = tenant_id;
    if (!effectiveTenantId && lockedSlug && tenant?.id && tenant.slug === lockedSlug) {
      effectiveTenantId = tenant.id;
    }
    const { data } = await api.post("/auth/verify-otp", {
      phone, otp, role, tenant_id: effectiveTenantId, ...extras,
    });
    if (data.needs_registration) return data; // FE will collect name & retry
    localStorage.setItem("fa_token", data.token);
    localStorage.setItem("fa_user", JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    if (data.user.tenant_id) {
      try {
        const t = await api.get(`/tenant/${data.user.tenant_id}/branding`);
        if (t.data?.id) {
          localStorage.setItem("fa_tenant", JSON.stringify(t.data));
          setTenant(t.data);
        }
      } catch {}
    }
    return data.user;
  }, [tenant]);

  const sendOtp = useCallback(async (phone, role, tenant_id, channel) => {
    const lockedSlug = localStorage.getItem("fa_slug");
    let effectiveTenantId = tenant_id;
    if (!effectiveTenantId && lockedSlug && tenant?.id && tenant.slug === lockedSlug) {
      effectiveTenantId = tenant.id;
    }
    const { data } = await api.post("/auth/send-otp", { phone, role, tenant_id: effectiveTenantId, channel });
    return data;
  }, [tenant]);

  const logout = useCallback(() => {
    localStorage.removeItem("fa_token");
    localStorage.removeItem("fa_user");
    // Keep fa_slug + fa_tenant so the white-label branding persists after logout
    setUser(null);
    setToken(null);
  }, []);

  // Apply tenant branding CSS vars
  useEffect(() => {
    const root = document.documentElement;
    if (tenant?.primary_color) root.style.setProperty("--tenant-primary", tenant.primary_color);
    if (tenant?.accent_color) root.style.setProperty("--tenant-accent", tenant.accent_color);
  }, [tenant]);

  return (
    <AuthContext.Provider value={{ user, token, tenant, login, sendOtp, logout, setTenant }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
